/**
 * Amount reducer that handles all things related with the amount
 */

import _ from 'lodash'
import { ActionTypes as types } from '../constants'
import serverProps from '../utils/serverProps'
import {
  getPlaceholderForCurrencyCode,
  getUSNumberFormatter,
} from '../../lib/amountUtils'
import { getCurrencyPrecision } from '../../../../lib/utils/currencyShared'

// initial props
function getInitialState(props = serverProps) {
  const {
    currencyFormat: {
      decimal: currencyDecimal = '.',
      separator: currencySeparator = ',',
    } = {},
    sessionEligibility: { currencies: senderCurrencies = [] } = {},
    amount,
    // honor recipient data if we already have recipient information
    recipientData: {
      amount: recipientAmount,
      recipientCurrencies,
      recipientCurrencyMap = {},
      // currency code passed from external, e.g. donate
      currencyCode: externalCurrencyCode,
      // currency code from recipient details
      recipientCurrencyCode,
      recipientPaymentDestinationsMap = {},
    } = {},
    amountFormatted = '',
    currencySymbol = '',
    primaryCurrencyCode = '', // set in helpers.js
    currencyMap = {},
    currencyCode,
    minimumAmount = 0,
    requestCurrencyCode = '',
    requesterCurrencies = {},
  } = props

  const currencyPrecision = getCurrencyPrecision(primaryCurrencyCode)
  const requestCurrencyPrecision = getCurrencyPrecision(requestCurrencyCode)
  const formatUSNumber = getUSNumberFormatter(currencyDecimal)

  const initialState = {
    amount:
      recipientAmount ||
      amount ||
      getPlaceholderForCurrencyCode(primaryCurrencyCode, currencyDecimal),
    amountFormatted,

    fromCurrencyCode: primaryCurrencyCode,
    currencyCode:
      currencyCode ||
      externalCurrencyCode ||
      recipientCurrencyCode ||
      primaryCurrencyCode,
    currencySymbol:
      recipientCurrencyMap[recipientCurrencyCode] ||
      (senderCurrencies[0] && senderCurrencies[0].symbol) ||
      currencySymbol,
    currencyCodeList: recipientCurrencies || senderCurrencies.map(c => c.code),
    currencyPrecision,
    currencyDecimal,
    currencySeparator,
    currencyMap: _.isEmpty(recipientCurrencyMap)
      ? currencyMap
      : recipientCurrencyMap,
    recipientPaymentDestinationsMap,
    formatUSNumber,
    minimumAmount,
    request: {
      currencyCode: requestCurrencyCode,
      currencyMap: requesterCurrencies,
      currencyPrecision: requestCurrencyPrecision,
      currencyDecimal: currencyDecimal,
    },
    requestCurrencyCode,
  }

  return initialState
}

export default (state = getInitialState(), action) => {
  switch (action.type) {
    case types.EXTERNAL.RECEIVED_EXTERNAL_PARAMS: {
      const { amount, currencyCode } = action.data
      if (!amount || !currencyCode) {
        return state
      }
      return { ...state, amount, currencyCode }
    }

    case types.EXTERNAL.RECEIVED_DATA:
      return getInitialState({ ...state, ...action.data })

    case types.AMOUNT.CHANGE_AMOUNT:
      return {
        ...state,
        amount: action.data.amount,
      }

    case types.AMOUNT.CHANGE_CURRENCY:
      return {
        ...state,
        currencyCode: action.data.currencyCode,
        currencySymbol: state.currencyMap[action.data.currencyCode],
        currencyPrecision: getCurrencyPrecision(action.data.currencyCode),
      }

    case types.AMOUNT.CHANGE_REQUEST_CURRENCY:
      return {
        ...state,
        request: {
          ...state.request,
          currencySymbol:
            state.request.currencyMap[action.data.requestCurrencyCode],
          currencyPrecision: getCurrencyPrecision(
            action.data.requestCurrencyCode
          ),
          currencyCode: action.data.requestCurrencyCode,
        },
      }

    case types.AMOUNT.UPDATE_RECIPIENT_CURRENCIES:
      return {
        ...state,
        currencyCode: action.data.recipientCurrencyCode,
        currencyCodeList: action.data.recipientCurrencies,
        currencyMap: action.data.recipientCurrencyMap,
        recipientPaymentDestinationsMap:
          action.data.recipientPaymentDestinationsMap,
      }

    case types.AMOUNT.UPDATE_RECIPIENT_DESTINATION_MAP:
      return {
        ...state,
        recipientPaymentDestinationsMap:
          action.data.recipientPaymentDestinationsMap,
      }

    case types.TRANSACTION.REQUESTED_MONEY:
      return {
        ...state,
        amountFormatted: action.data.amountFormatted,
      }

    case types.TRANSACTION.SENT_MONEY:
      return {
        ...state,
        amountFormatted: action.data.amountFormatted,
      }

    case types.TRANSACTION.SUBMITTING_RECIPIENT:
    case types.TRANSACTION.LEAVING_SUCCESS_PAGE:
    case types.TRANSACTION.CANCELLED_TRANSACTION:
      // Restore initial currencyCode/Decimal because FX Fee Calculator might change them.
      // And if we don't reset them, currencyCode/Decimal will be default to the foreign
      // currencyCode/decimal if a user first send to a foreign recipient and then send to
      // a unilateral user.
      const {
        currencyDecimal,
        requestCurrencyCode,
        primaryCurrencyCode,
        currencyCodeList,
        recipientPaymentDestinationsMap,
      } = getInitialState()

      return {
        ...state,
        currencyCode: state.fromCurrencyCode || '',
        currencyDecimal,
        currencyCodeList,
        recipientPaymentDestinationsMap,
        amount: getPlaceholderForCurrencyCode(
          primaryCurrencyCode,
          currencyDecimal
        ),
        amountFormatted: '',
        request: {
          ...state.request,
          currencyCode: requestCurrencyCode,
          currencyDecimal: currencyDecimal,
        },
      }

    case types.CROSS_BORDER.SET_INITIAL_STATE:
      const newDefaultedCurrencyCode =
        action.data.toCurrencyCodeList[0] || state.currencyCode
      return {
        ...state,
        currencyCode: newDefaultedCurrencyCode,
        currencySymbol:
          action.data.toCurrencySymbolMap[newDefaultedCurrencyCode],
        currencyMap: action.data.toCurrencySymbolMap,
        fromCurrencyCode: action.data.fromCurrencyCode,
      }
    case types.CROSS_BORDER.CHANGE_ORIGIN_CURRENCY_CODE:
      return {
        ...state,
        fromCurrencyCode: action.data.currencyCode,
      }
    case types.CROSS_BORDER.CHANGE_ORIGIN_AMOUNT:
    case types.CROSS_BORDER.CHANGE_DESTINATION_AMOUNT:
      return {
        ...state,
        amount: action.data.amountIsZero ? '0' : state.amount,
      }
    default:
      return state
  }
}
